import type { MenuDTO, MenuProductDTO, ComboDTO, MenuOptionGroupDTO, MenuOptionItemDTO } from "../dto/menu.dto";
import type { Category, Product, OptionGroup, OptionItem, ResolvedCombo } from "../interfaces/domain/menu";

type Mode = "LOCAL" | "DELIVERY";

const safeStr = (v: unknown) => (typeof v === "string" ? v : "");
const safeNum = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const pos = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : 0);

const mapOptionItem = (it: MenuOptionItemDTO): OptionItem => ({
  id: String(it?.id ?? ""),
  name: safeStr(it?.name),
  price: safeNum(it?.price),
  is_available: it?.is_enabled !== false,
  position: safeNum(it?.position),
});

const mapOptionGroup = (g: MenuOptionGroupDTO): OptionGroup => ({
  id: String(g?.id ?? ""),
  name: safeStr(g?.name),
  adds_price: g?.adds_price === true,
  min_selection: safeNum(g?.min_selection),
  max_selection: Number.isFinite(g?.max_selection) ? g.max_selection : 10,
  position: safeNum(g?.position),
  items: g.items.filter((x) => x.is_enabled !== false).map(mapOptionItem),
});

export function mapDTOToDomain(dto: MenuDTO | null, mode: Mode): Category[] {
  if (!dto?.categories) return [];

  const categories = [...dto.categories]
    .sort((a, b) => pos(a.position) - pos(b.position))
    .map((catDTO) => {
      const products = [...(catDTO.products ?? [])]
        .map((p) => mapProduct(p, mode))
        .filter((p) => (mode === "DELIVERY" ? (p.price ?? 0) > 0 : true))
        .sort((a, b) => pos(a.position) - pos(b.position));

      return {
        id: catDTO.id,
        name: catDTO.name,
        description: catDTO.description ?? undefined,
        position: pos(catDTO.position),
        products,
      };
    });

  return mode === "DELIVERY"
    ? categories.filter((c) => c.products.length > 0)
    : categories;
}

function mapProduct(p: MenuProductDTO, mode: Mode): Product {
  const price = typeof p.price === "number" ? p.price : 0;
  const imageUrl = p.image || null;

  return {
    id: String(p.id),
    title: p.title,
    description: p.description ?? undefined,
    price: price > 0 ? price : null,
    image_url: typeof imageUrl === "string" && imageUrl.trim() ? imageUrl : null,
    tags: p.tags ?? [],
    position: pos(p.position),
    is_available: mode === "DELIVERY" ? price > 0 : true,
    needs_price_quote: p.needs_price_quote ?? !(price > 0),
    options: p.options.map(mapOptionGroup),
    combo: resolveCombo(p.combo_items),
  };
}

function resolveCombo(combo: ComboDTO | undefined): ResolvedCombo | undefined {
  if (!combo?.items?.length) return undefined;

  const items = combo.items.map((item) => {
    if (item.type === "fixed") {
      return {
        type: "fixed" as const,
        productId: item.productId,
        quantity: item.quantity,
        title: item.title,
        image_url: item.image ?? null,
      };
    }

    // type === "select": choices resueltos por el SQL inline
    return {
      type: "select" as const,
      categoryId: item.categoryId,
      quantity: item.quantity,
      min: item.min,
      max: item.max,
      label: item.label,
      choices: (item.choices ?? []).map((c) => ({
        id: String(c.id),
        title: c.title,
        description: c.description ?? undefined,
        image_url: c.image ?? null,
      })),
    };
  });

  return { items };
}
